import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  Heading,
  Text,
  Button,
  Spinner,
  HStack,
  Box,
  Progress,
  useColorModeValue,
  keyframes,
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, CloseIcon, TimeIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import type { AlmaPaymentStep, AlmaStepConfig } from 'types/alma';

interface AlmaPaymentOverlayProps {
  isOpen: boolean;
  currentStep: AlmaPaymentStep;
  currentAttempt: number;
  maxAttempts: number;
  isSlowNetwork: boolean;
  error: string | null;
  errorStep: AlmaPaymentStep | null;
  onCancel: () => void;
}

// Animation de pulsation pour le spinner
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

// Configuration des messages pour chaque étape
const STEP_CONFIGS: Record<AlmaPaymentStep, AlmaStepConfig> = {
  verifying: {
    title: 'Vérification de votre paiement',
    description: 'Nous contactons Alma pour valider votre transaction...',
    color: 'blue',
    showProgress: true,
    showAttempts: true,
  },
  processing: {
    title: 'Paiement validé !',
    description: 'Préparation de vos crédits...',
    color: 'green',
    showProgress: false,
    showAttempts: false,
  },
  crediting: {
    title: 'Ajout de vos crédits',
    description: 'Nous créditons votre compte Mindbody...',
    color: 'green',
    showProgress: false,
    showAttempts: false,
  },
  success: {
    title: "C'est terminé !",
    description: 'Redirection vers vos crédits...',
    color: 'green',
    showProgress: false,
    showAttempts: false,
  },
  rolling_back: {
    title: 'Annulation en cours',
    description: 'Votre paiement va être automatiquement remboursé...',
    color: 'orange',
    showProgress: false,
    showAttempts: false,
  },
  failed: {
    title: 'Paiement non validé',
    description: 'Vous serez remboursé sous 48h.',
    color: 'red',
    showProgress: false,
    showAttempts: false,
  },
};

export function AlmaPaymentOverlay({
  isOpen,
  currentStep,
  currentAttempt,
  maxAttempts,
  isSlowNetwork,
  error,
  errorStep,
  onCancel,
}: AlmaPaymentOverlayProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const config = STEP_CONFIGS[currentStep];
  const progressValue = (currentAttempt / maxAttempts) * 100;

  // Icône animée en fonction de l'étape
  const renderIcon = () => {
    switch (currentStep) {
      case 'verifying':
        return (
          <Spinner
            size="lg"
            color={`${config.color}.500`}
            thickness="3px"
            speed="0.8s"
            animation={`${pulse} 2s ease-in-out infinite`}
          />
        );
      case 'processing':
        return (
          <MotionBox
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              w={16}
              h={16}
              borderRadius="full"
              bg="green.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CheckIcon w={8} h={8} color="green.600" />
            </Box>
          </MotionBox>
        );
      case 'crediting':
        return (
          <MotionBox
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              w={16}
              h={16}
              borderRadius="full"
              bg="green.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner size="lg" color="green.500" thickness="3px" speed="0.8s" />
            </Box>
          </MotionBox>
        );
      case 'success':
        return (
          <MotionBox
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              w={16}
              h={16}
              borderRadius="full"
              bg="green.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CheckIcon w={8} h={8} color="green.600" />
            </Box>
          </MotionBox>
        );
      case 'rolling_back':
        return (
          <MotionBox
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              w={16}
              h={16}
              borderRadius="full"
              bg="orange.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <WarningIcon w={8} h={8} color="orange.600" />
            </Box>
          </MotionBox>
        );
      case 'failed':
        return (
          <MotionBox
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              w={16}
              h={16}
              borderRadius="full"
              bg="red.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CloseIcon w={6} h={6} color="red.600" />
            </Box>
          </MotionBox>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Bloque la fermeture automatique
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="md"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="2xl" boxShadow="2xl" mx={4}>
        <ModalBody p={8}>
          <AnimatePresence mode="wait">
            <MotionVStack
              key={currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              spacing={6}
            >
              {/* Header avec icône et titre */}
              <VStack spacing={4}>
                <Box position="relative">
                  {currentStep === 'verifying' ? (
                    <Box
                      w={16}
                      h={16}
                      borderRadius="full"
                      bg="blue.50"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      position="relative"
                    >
                      <Spinner
                        size="lg"
                        color="blue.500"
                        thickness="3px"
                        speed="0.8s"
                        animation={`${pulse} 2s ease-in-out infinite`}
                      />
                      <Box
                        position="absolute"
                        w={8}
                        h={8}
                        bg="white"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <img
                          src={`${import.meta.env.BASE_URL}logoalma.svg`}
                          alt="Alma"
                          style={{ height: '20px', width: 'auto' }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    renderIcon()
                  )}
                </Box>

                <VStack spacing={2} textAlign="center">
                  <Heading size="lg" color="gray.800">
                    {config.title}
                  </Heading>
                  <Text color="gray.600" fontSize="md">
                    {config.description}
                  </Text>
                </VStack>
              </VStack>

              {/* Progress et statut */}
              {config.showProgress && (
                <VStack spacing={4} w="full">
                  <Box w="full">
                    {config.showAttempts && (
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm" color="gray.500">
                          Tentative {currentAttempt} sur {maxAttempts}
                        </Text>
                        <HStack spacing={1}>
                          <TimeIcon w={3} h={3} color="gray.400" />
                          <Text fontSize="sm" color="gray.500">
                            Quelques secondes
                          </Text>
                        </HStack>
                      </HStack>
                    )}
                    <Progress
                      value={progressValue}
                      colorScheme={config.color}
                      bg="gray.100"
                      borderRadius="full"
                      size="sm"
                      hasStripe
                      isAnimated
                    />
                  </Box>
                </VStack>
              )}

              {/* Messages informatifs */}
              <VStack spacing={3} textAlign="center" maxW="sm">
                {currentStep === 'verifying' && (
                  <>
                    <HStack spacing={2}>
                      <CheckIcon w={4} h={4} color="green.500" />
                      <Text fontSize="sm" color="gray.600">
                        Votre paiement Alma a été effectué avec succès
                      </Text>
                    </HStack>

                    <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                      Nous finalisons l'ajout de vos crédits à votre compte.
                      <br />
                      <strong>Merci de ne pas fermer cette page.</strong>
                    </Text>

                    {isSlowNetwork && (
                      <MotionBox
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                      >
                        <HStack
                          spacing={2}
                          bg="orange.50"
                          p={3}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="orange.200"
                        >
                          <WarningIcon w={4} h={4} color="orange.500" />
                          <Text fontSize="sm" color="orange.700" fontWeight="medium">
                            Votre connexion semble lente, merci de patienter...
                          </Text>
                        </HStack>
                      </MotionBox>
                    )}
                  </>
                )}

                {currentStep === 'processing' && (
                  <MotionBox
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <VStack spacing={2}>
                      <Text fontSize="md" color="green.600" fontWeight="semibold">
                        ✅ Paiement Alma confirmé
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Préparation de l'ajout des crédits...
                      </Text>
                    </VStack>
                  </MotionBox>
                )}

                {currentStep === 'crediting' && (
                  <MotionBox
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <VStack spacing={2}>
                      <Text fontSize="md" color="green.600" fontWeight="semibold">
                        💳 Crédit en cours
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Ajout de vos crédits à votre compte Mindbody...
                      </Text>
                      <Text fontSize="xs" color="gray.400" fontStyle="italic">
                        Merci de ne pas fermer cette page
                      </Text>
                    </VStack>
                  </MotionBox>
                )}

                {currentStep === 'success' && (
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        ✨ Tout est prêt !
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Redirection en cours...
                      </Text>
                    </VStack>
                  </MotionBox>
                )}

                {currentStep === 'rolling_back' && (
                  <MotionBox
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <VStack spacing={2}>
                      <Text fontSize="sm" color="orange.600" fontWeight="medium">
                        Remboursement automatique en cours
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Vous recevrez un email de confirmation
                      </Text>
                    </VStack>
                  </MotionBox>
                )}

                {currentStep === 'failed' && (
                  <MotionBox
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <VStack spacing={4}>
                      <Text fontSize="sm" color="red.600" fontWeight="medium">
                        {error || "Le paiement n'a pas pu être validé"}
                      </Text>

                      {errorStep && (
                        <Box bg="red.50" p={3} borderRadius="md" border="1px solid" borderColor="red.200" w="full">
                          <Text fontSize="xs" color="red.700" fontWeight="semibold" mb={1}>
                            Étape de l'échec :
                          </Text>
                          <Text fontSize="xs" color="red.600">
                            {errorStep === 'verifying' && '📝 Paiement avec Alma'}
                            {errorStep === 'processing' && '📝 Validation du paiement Alma'}
                            {errorStep === 'crediting' && '💳 Crédit de votre compte'}
                          </Text>
                          <Text fontSize="xs" color="gray.600" mt={2}>
                            {errorStep === 'crediting'
                              ? "Votre paiement a été validé mais nous n'avons pas pu créditer votre compte. Votre paiement sera remboursé automatiquement."
                              : "Le paiement Alma n'a pas pu être validé. Vous serez automatiquement remboursé."}
                          </Text>
                        </Box>
                      )}

                      <VStack spacing={2} w="full">
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          Si vos crédits n'apparaissent pas après 10 minutes :
                        </Text>

                        <Button
                          as="a"
                          href="/contact/"
                          size="sm"
                          colorScheme="blue"
                          variant="solid"
                          w="full"
                          target="_blank"
                        >
                          Contacter le support
                        </Button>

                        <Text fontSize="xs" color="gray.400" textAlign="center" fontStyle="italic">
                          Merci de communiquer le numéro de paiement et l'étape de l'échec ci-dessus
                        </Text>
                      </VStack>
                    </VStack>
                  </MotionBox>
                )}
              </VStack>

              {/* Actions */}
              {currentStep === 'verifying' && (
                <VStack spacing={3} w="full" pt={4}>
                  <Text fontSize="xs" color="gray.400" textAlign="center">
                    En cas de problème, votre paiement sera automatiquement remboursé
                  </Text>

                  <Button
                    variant="ghost"
                    size="sm"
                    color="gray.500"
                    onClick={onCancel}
                    _hover={{
                      bg: 'red.50',
                      color: 'red.600',
                    }}
                  >
                    Annuler et revenir aux tarifs
                  </Button>
                </VStack>
              )}

              {(currentStep === 'failed' || currentStep === 'rolling_back') && (
                <VStack spacing={3} w="full" pt={4}>
                  <Button
                    colorScheme="gray"
                    size="md"
                    w="full"
                    onClick={() => {
                      window.location.href = '/account/services';
                    }}
                  >
                    {currentStep === 'failed' ? 'Fermer et voir mes crédits' : 'Fermer'}
                  </Button>
                </VStack>
              )}
            </MotionVStack>
          </AnimatePresence>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
